import { CheckCircle2, FlaskConical, Info } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  MISSING_LAB_VALUES_HINT,
  MISSING_LAB_VALUES_TITLE,
  type MissingLabValueRow,
} from '@/lib/limsInputs'

/**
 * Lab values a split could not find on the forms, with the two ways forward:
 * type the value from the lab report, or order the test.
 *
 * A notice, not an error: the split is not blocked by it (z8nrz7fcu4
 * follow-up). The operator holds the report; a form configured without a place
 * for the value is not a reason to stop them.
 */
export function MissingLabValues({
  rows,
  stillMissing,
  values,
  onChange,
  onOrderTest,
}: {
  /** Every requirement seen for this product, including ones typed since. */
  rows: MissingLabValueRow[]
  /** Keys of the rows nothing satisfies yet, per the latest preview. */
  stillMissing: ReadonlySet<string>
  /** Typed text by system name. */
  values: Record<string, string>
  onChange: (systemName: string, text: string) => void
  /** Absent when ordering is not available here; the button is then hidden. */
  onOrderTest?: () => void
}) {
  if (rows.length === 0) return null
  const open = rows.filter(r => stillMissing.has(r.key)).length

  return (
    <div className='rounded-md border border-amber-300 bg-amber-50 px-3 py-2.5 text-sm dark:border-amber-500/40 dark:bg-amber-500/10'>
      <div className='flex items-start justify-between gap-3'>
        <p className='flex items-center gap-2 font-medium text-amber-900 dark:text-amber-300'>
          {open > 0 ? <Info className='h-4 w-4 shrink-0' /> : <CheckCircle2 className='h-4 w-4 shrink-0' />}
          {open > 0 ? MISSING_LAB_VALUES_TITLE : 'Lab values entered by hand'}
        </p>
        {onOrderTest && open > 0 && (
          <Button type='button' variant='outline' size='sm' className='h-7 shrink-0 bg-background' onClick={onOrderTest}>
            <FlaskConical className='mr-1.5 h-3.5 w-3.5' />
            Order test
          </Button>
        )}
      </div>

      <div className='mt-2 space-y-2'>
        {rows.map(row => (
          <div key={row.key} className='flex flex-wrap items-start gap-2'>
            {row.options.map((o, i) => (
              <div key={o.systemName} className='flex items-start gap-2'>
                {i > 0 && <span className='pt-6 text-xs text-muted-foreground'>or</span>}
                <LabValueInput
                  systemName={o.systemName}
                  label={o.label}
                  hint={stillMissing.has(row.key) ? o.why : 'Entered by hand'}
                  value={values[o.systemName] ?? ''}
                  onChange={onChange}
                />
              </div>
            ))}
          </div>
        ))}
      </div>

      {open > 0 && <p className='mt-2 text-xs text-muted-foreground'>{MISSING_LAB_VALUES_HINT}</p>}
    </div>
  )
}

function LabValueInput({
  systemName,
  label,
  hint,
  value,
  onChange,
}: {
  systemName: string
  label: string
  hint: string
  value: string
  onChange: (systemName: string, text: string) => void
}) {
  const id = `lab-value-${systemName}`
  return (
    <div className='w-44'>
      <label
        htmlFor={id}
        className='block text-xs font-medium text-foreground'
        title={`Field Library system name: ${systemName}`}
      >
        {label}
      </label>
      <Input
        id={id}
        type='number'
        inputMode='decimal'
        min={0}
        step='any'
        className='mt-0.5 h-8 bg-background'
        placeholder='Enter value'
        value={value}
        onChange={e => onChange(systemName, e.target.value)}
      />
      <p className='mt-0.5 text-[11px] text-muted-foreground'>{hint}</p>
    </div>
  )
}
